/**
 * Every document the business has produced.
 *
 * A list rather than a gallery, because this is the one screen where somebody
 * arrives knowing what they want: the invoice they made on Tuesday. So it is
 * ordered by when, it says how many pages and how big, and the download is one
 * click from the row.
 *
 * It also keeps polling while anything is still being produced, which is the whole
 * of the "never leave a user wondering whether something worked" requirement for
 * this screen — a document queued in another tab appears here as queued and then as
 * ready, without anybody reloading.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type FolderRow, type ReportSummary } from "../api.js";
import { reportFailure } from "../App.js";
import { changed, useChanges } from "../changes.js";
import { FolderCrumbs, FolderTableRow } from "../shell/Folders.js";
import { folderView } from "../shell/tree.js";
import { Button, Confirm, Empty, Icon, Note, Skeleton, ago, bytes, count, toast, when } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";
import { ShareSheet } from "./ShareSheet.js";

export function Documents({ folder }: { folder: string | null }) {
  const { view, refresh } = useWorkspace();
  const changes = useChanges();
  const [reports, setReports] = useState<ReportSummary[] | null>(null);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [deleting, setDeleting] = useState<ReportSummary | null>(null);
  const [sharing, setSharing] = useState<ReportSummary | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Bumped to reload the list, which is how a change in the share sheet reaches the rows behind it. */
  const [version, setVersion] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [{ reports: loaded }, { folders: filed }] = await Promise.all([api.reports(), api.folders()]);
        if (cancelled) return;
        setReports(loaded);
        setFolders(filed);
        // Keep looking only while something is actually in flight. A list that
        // polls for ever is a list that costs a request every few seconds for the
        // rest of the afternoon.
        const working = loaded.some((r) => r.status === "queued" || r.status === "rendering");
        if (working) timer.current = setTimeout(load, 1500);
      } catch (e) {
        if (!cancelled) reportFailure(e, "We couldn't load your documents.");
      }
    };
    void load();

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [version, changes]);

  const place = useMemo(
    () =>
      folderView(
        folders.filter((f) => f.section === "documents"),
        (reports ?? []).map((r) => ({ id: r.id, name: r.name, folderId: r.folderId, updatedAt: r.createdAt })),
        folder
      ),
    [folders, reports, folder]
  );

  const download = async (report: ReportSummary) => {
    setBusy(report.id);
    try {
      const { url } = await api.downloadReport(report.id);
      window.location.href = url;
    } catch (e) {
      reportFailure(e, "We couldn't get that document.");
    } finally {
      setBusy(null);
    }
  };

  if (!reports) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 36, width: 220 }} />
        <Skeleton rows={3} height={70} />
      </div>
    );
  }

  if (!reports.length) {
    return (
      <div className="wrap">
        <Empty
          title="No documents yet"
          actions={
            <a className="btn btn--primary btn--lg" href="/designs">
              <Icon name="design" /> Open a design
            </a>
          }
        >
          Every PDF you produce is kept here, so you can send it again without making it again. Open a design and press “Make the document”.
        </Empty>
      </div>
    );
  }

  const thisMonth = view.usage.reportsThisMonth;

  return (
    <div className="wrap stack" style={{ gap: 20 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">Documents</span>
          <h1>
            {count(reports.length)} document{reports.length === 1 ? "" : "s"}
          </h1>
          <p className="head__lede">
            {count(thisMonth)} of {count(view.limits.reportsPerMonth)} produced this month. Everything here stays downloadable, whatever your plan does
            next.
          </p>
        </div>
        <div className="head__actions">
          <a className="btn" href="/designs">
            Make another
          </a>
        </div>
      </div>

      {view.plan.marked ? (
        <Note tone="info" title="Your documents carry a small BroadPaper line">
          One line at the foot of each page, on the free plan. <a href="/account">Removing it</a> is the only thing paying changes about the document.
        </Note>
      ) : null}

      <FolderCrumbs base="/documents" label="All documents" crumbs={place.crumbs} />

      <div className="table__scroll" style={{ maxHeight: "none" }}>
        <table className="table">
          <thead>
            <tr>
              <th>Document</th>
              <th style={{ width: 130 }}>Made</th>
              <th className="num" style={{ width: 90 }}>
                Pages
              </th>
              <th className="num" style={{ width: 90 }}>
                Size
              </th>
              <th style={{ width: 300 }} />
            </tr>
          </thead>
          <tbody>
            {place.folders.map((f) => (
              <FolderTableRow key={f.id} base="/documents" folder={f} span={5} noun="document" />
            ))}
            {reports.filter((r) => place.itemIds.has(r.id)).map((report) => (
              <tr key={report.id}>
                <td>
                  <div className="stack" style={{ gap: 3 }}>
                    <strong>{report.name.replace(/\.pdf$/, "")}</strong>
                    {report.status === "failed" ? (
                      <span className="small" style={{ color: "var(--red-deep)" }}>
                        {report.error ?? "Could not be produced."}
                      </span>
                    ) : report.marked ? (
                      <span className="small muted">Carries the BroadPaper line</span>
                    ) : null}
                    {report.sharing.total ? (
                      <button type="button" className="link-button small" style={{ alignSelf: "flex-start" }} onClick={() => setSharing(report)}>
                        <Icon name="share" className="inline-icon" />{" "}
                        {report.sharing.active ? "Shared" : "Links stopped"} · {count(report.sharing.views)} view{report.sharing.views === 1 ? "" : "s"}
                        {report.sharing.lastViewedAt ? `, last ${ago(report.sharing.lastViewedAt)}` : ""}
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="small muted">{when(report.createdAt)}</td>
                <td className="num">{report.pages ?? "—"}</td>
                <td className="num small muted">{report.bytes === null ? "—" : bytes(report.bytes)}</td>
                <td>
                  <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                    {report.status === "ready" ? (
                      <>
                        <Button size="sm" variant="primary" icon="download" busy={busy === report.id} onClick={() => void download(report)}>
                          Download
                        </Button>
                        <Button size="sm" icon="share" onClick={() => setSharing(report)} title="Share a link to this document">
                          Share
                        </Button>
                        <Button size="sm" variant="quiet" icon="trash" onClick={() => setDeleting(report)} title="Delete this document" />
                      </>
                    ) : report.status === "failed" ? (
                      <>
                        <a className="btn btn--sm" href={`/designs/${report.templateId}`}>
                          Open the design
                        </a>
                        <Button size="sm" variant="quiet" icon="trash" onClick={() => setDeleting(report)} title="Delete this document" />
                      </>
                    ) : (
                      <span className="row small muted" style={{ gap: 8 }}>
                        <span className="spinner" /> {report.status === "rendering" ? "Being produced…" : "In the queue…"}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!place.folders.length && !reports.some((r) => place.itemIds.has(r.id)) ? <p className="muted">Nothing in this folder yet. Move a document here from the sidebar.</p> : null}

      {sharing ? <ShareSheet report={sharing} onClose={() => setSharing(null)} onChanged={() => setVersion((v) => v + 1)} /> : null}

      {deleting ? (
        <Confirm
          title={deleting.sharing.active ? `Delete “${deleting.name.replace(/\.pdf$/, "")}” and stop its links?` : `Delete “${deleting.name.replace(/\.pdf$/, "")}”?`}
          confirmLabel={deleting.sharing.active ? "Delete it and stop the links" : "Delete it"}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const target = deleting;
            setDeleting(null);
            try {
              await api.deleteReport(target.id, { shared: target.sharing.active > 0 });
              setReports((current) => (current ?? []).filter((r) => r.id !== target.id));
              await refresh();
              changed();
              toast(target.sharing.active ? "Deleted. Its links no longer work." : "Deleted.", "good");
            } catch (e) {
              // Somebody made a link in another tab after this list loaded. Ask again,
              // with the warning this time, rather than failing with a message about it.
              if (e instanceof ApiError && e.code === "conflict" && typeof e.detail?.activeShares === "number") {
                setDeleting({ ...target, sharing: { ...target.sharing, active: e.detail.activeShares as number } });
              } else reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          {deleting.sharing.active
            ? `It has ${deleting.sharing.active === 1 ? "a share link that still works" : `${deleting.sharing.active} share links that still work`}${deleting.sharing.views ? `, opened ${count(deleting.sharing.views)} time${deleting.sharing.views === 1 ? "" : "s"}` : ""}. Deleting the document stops ${deleting.sharing.active === 1 ? "it" : "them"} working — anybody you sent ${deleting.sharing.active === 1 ? "it" : "them"} to will be told the document is no longer available. `
            : ""}
          The file goes for good. The design it came from is not affected, so you can make it again — it will count against this month's documents.
        </Confirm>
      ) : null}
    </div>
  );
}
